export type TrustLevel = "trustworthy" | "caution" | "low";
export type QualityLevel = "good" | "moderate" | "bad";

import { GP50_STRAIGHT_LINE_DB_COLUMN_LABELS } from "./gp50StraightLineCheatSheetChannels";

export interface PerformanceDriversFit {
  rmse: number;
  rmse_train: number;
  rmse_residual?: number;
  rmse_residual_train?: number;
  rmse_surface?: number;
  rmse_surface_train?: number;
  training_mode?: string;
  model_n_samples?: number;
  shap_bin_samples?: number;
  parity_points?: Array<{ actual: number; predicted: number }>;
}

export interface PerformanceDriversCell {
  n_samples: number;
  n_boats: number;
  skipped: boolean;
  skip_reason?: string;
  rmse?: number | null;
  rmse_train?: number | null;
  fit?: PerformanceDriversFit | null;
}

export type PerformanceDriversRmseRating = "trustworthy" | "moderate" | "low";

export interface PerformanceDriversFitRating {
  rating: PerformanceDriversRmseRating;
  label: string;
  hint?: string;
}

export function performanceDriversTrustBadgeClass(
  rating: PerformanceDriversRmseRating
): string {
  if (rating === "trustworthy") return "performance-drivers-trust-trustworthy";
  if (rating === "moderate") return "performance-drivers-trust-caution";
  return "performance-drivers-trust-low";
}

export interface ShapBeeswarmFeaturePoint {
  shap: number;
  value: number | null;
  norm: number;
}

export interface ShapBeeswarmFeature {
  name: string;
  label: string;
  mean_abs_shap: number;
  points: ShapBeeswarmFeaturePoint[];
}

export interface TrustBadge {
  level: TrustLevel;
  label: string;
  detail: string;
}

export interface QualityBadge {
  level: QualityLevel;
  label: string;
}

const RMSE_TRUSTWORTHY_MAX = 3;
const RMSE_CAUTION_MAX = 6;

const SEGMENTS_GOOD_MIN = 500;
const SEGMENTS_MODERATE_MIN = 200;
const BOATS_GOOD_MIN = 4;
const BOATS_MODERATE_MIN = 2;
const TEST_SEGMENTS_GOOD_MIN = 80;
const TEST_SEGMENTS_MODERATE_MIN = 40;

const OVERFIT_CAUTION_RATIO = 1.5;

/** Reference-style labels for GP50 (aligned with cheat sheet straight-line headers). */
export const GP50_BEESWARM_LABELS = GP50_STRAIGHT_LINE_DB_COLUMN_LABELS;

const PERFORMANCE_FEATURE_NAMES = new Set(["Tws_kph", "Twa_n_deg", "Bsp_kph", "Lwy_n_deg"]);

function rmseTrustFromValue(rmse: number): TrustBadge {
  if (rmse <= RMSE_TRUSTWORTHY_MAX) {
    return {
      level: "trustworthy",
      label: "Trustworthy",
      detail: `Holdout RMSE ${rmse.toFixed(1)} VMG%`,
    };
  }
  if (rmse <= RMSE_CAUTION_MAX) {
    return {
      level: "caution",
      label: "Use caution",
      detail: `Holdout RMSE ${rmse.toFixed(1)} VMG%`,
    };
  }
  return {
    level: "low",
    label: "Low trust",
    detail: `Holdout RMSE ${rmse.toFixed(1)} VMG%`,
  };
}

function trustLevelToFitRating(level: TrustLevel): PerformanceDriversRmseRating {
  if (level === "trustworthy") return "trustworthy";
  if (level === "caution") return "moderate";
  return "low";
}

function worstTrustLevel(a: TrustLevel, b: TrustLevel): TrustLevel {
  const rank: Record<TrustLevel, number> = { low: 0, caution: 1, trustworthy: 2 };
  return rank[a] <= rank[b] ? a : b;
}

function overfitTrustLevel(testRmse: number, trainRmse: number): TrustLevel {
  const overfitRatio = trainRmse > 0 ? testRmse / trainRmse : 1;
  if (overfitRatio > OVERFIT_CAUTION_RATIO) return "caution";
  return "trustworthy";
}

/** Merge holdout-RMSE tier with generalization gap. Overfit alone never forces low trust. */
function combineTrustLevels(holdoutLevel: TrustLevel, overfitLevel: TrustLevel): TrustLevel {
  return worstTrustLevel(holdoutLevel, overfitLevel);
}

function cellHasInsufficientData(cell: PerformanceDriversCell): boolean {
  const nSamples = cell.fit?.model_n_samples ?? cell.n_samples;
  const testCount = cell.fit?.parity_points?.length ?? 0;
  return (
    getSegmentQualityBadge(nSamples).level === "bad" ||
    getBoatQualityBadge(cell.n_boats).level === "bad" ||
    getTestSegmentQualityBadge(testCount).level === "bad"
  );
}

function insufficientDataDetail(cell: PerformanceDriversCell): string {
  const nSamples = cell.fit?.model_n_samples ?? cell.n_samples;
  const testCount = cell.fit?.parity_points?.length ?? 0;
  return (
    `Insufficient training data for this cell (${nSamples} segments, ` +
    `${cell.n_boats} boats, ${testCount} holdout segments).`
  );
}

function confidenceDetail(testRmse: number, trainRmse: number, level: TrustLevel): string {
  const overfitRatio = trainRmse > 0 ? testRmse / trainRmse : 1;
  const base = `Holdout RMSE ${testRmse.toFixed(1)} · training ${trainRmse.toFixed(1)}`;
  if (level === "trustworthy" || overfitRatio <= OVERFIT_CAUTION_RATIO) {
    return base;
  }
  return `${base} · holdout/training ratio ${overfitRatio.toFixed(1)}`;
}

export function resolvePerformanceDriversTestRmse(
  fit?: PerformanceDriversFit | null,
  rmse?: number | null
): number | null {
  if (fit?.rmse != null && Number.isFinite(fit.rmse)) return fit.rmse;
  if (rmse != null && Number.isFinite(rmse)) return rmse;
  return null;
}

export function resolvePerformanceDriversTrainRmse(
  fit?: PerformanceDriversFit | null,
  rmseTrain?: number | null
): number | null {
  if (fit?.rmse_train != null && Number.isFinite(fit.rmse_train)) return fit.rmse_train;
  if (rmseTrain != null && Number.isFinite(rmseTrain)) return rmseTrain;
  return null;
}

/** Single confidence rating using holdout RMSE and train/holdout generalization gap. */
export function getPerformanceDriversConfidenceBadge(
  cell: PerformanceDriversCell | null | undefined
): TrustBadge {
  if (!cell || cell.skipped || !cell.fit) {
    return {
      level: "low",
      label: "Low trust",
      detail: cell?.skip_reason ?? "Model not available for this cell.",
    };
  }

  if (cellHasInsufficientData(cell)) {
    return {
      level: "low",
      label: "Low trust",
      detail: insufficientDataDetail(cell),
    };
  }

  const testRmse = resolvePerformanceDriversTestRmse(cell.fit, cell.rmse) ?? 0;
  const trainRmse = resolvePerformanceDriversTrainRmse(cell.fit, cell.rmse_train) ?? 0;
  const holdoutBadge = rmseTrustFromValue(testRmse);
  const combinedLevel = combineTrustLevels(
    holdoutBadge.level,
    overfitTrustLevel(testRmse, trainRmse)
  );

  return {
    level: combinedLevel,
    label:
      combinedLevel === "trustworthy"
        ? "Trustworthy"
        : combinedLevel === "caution"
          ? "Use caution"
          : "Low trust",
    detail: confidenceDetail(testRmse, trainRmse, combinedLevel),
  };
}

export function classifyPerformanceDriversConfidence(
  cell: PerformanceDriversCell | null | undefined
): PerformanceDriversFitRating {
  const badge = getPerformanceDriversConfidenceBadge(cell);
  return {
    rating: trustLevelToFitRating(badge.level),
    label: badge.label,
    hint: badge.detail,
  };
}

export function classifyPerformanceDriversRmse(
  rmse: number | null | undefined
): PerformanceDriversFitRating {
  if (rmse == null || !Number.isFinite(rmse)) {
    return {
      rating: "low",
      label: "Low trust",
      hint: "Model not available for this cell.",
    };
  }
  const badge = rmseTrustFromValue(rmse);
  return {
    rating: trustLevelToFitRating(badge.level),
    label: badge.label,
    hint: badge.detail,
  };
}

export function normalizeShapBeeswarmFeatures(
  features: Array<
    Partial<ShapBeeswarmFeature> & {
      display_name?: string;
      points?: Array<Partial<ShapBeeswarmFeaturePoint> & { value?: number | null }>;
    }
  >
): ShapBeeswarmFeature[] {
  return (features ?? []).map((feat) => {
    const rawPoints = feat.points ?? [];
    const numericValues = rawPoints
      .map((p) => p.value)
      .filter((v): v is number => v != null && Number.isFinite(v));
    const min = numericValues.length > 0 ? Math.min(...numericValues) : 0;
    const max = numericValues.length > 0 ? Math.max(...numericValues) : 1;

    const points: ShapBeeswarmFeaturePoint[] = rawPoints.map((p) => {
      const value = p.value ?? null;
      let norm = p.norm;
      if (norm == null || !Number.isFinite(norm)) {
        if (value == null || !Number.isFinite(value) || min === max) {
          norm = 0.5;
        } else {
          norm = (value - min) / (max - min);
        }
      }
      return {
        shap: p.shap ?? 0,
        value,
        norm: Math.max(0, Math.min(1, norm)),
      };
    });

    return {
      name: feat.name ?? "",
      label:
        GP50_STRAIGHT_LINE_DB_COLUMN_LABELS[feat.name ?? ""] ??
        feat.label ??
        feat.display_name ??
        feat.name ??
        "",
      mean_abs_shap: feat.mean_abs_shap ?? 0,
      points,
    };
  });
}

export const PERFORMANCE_DRIVERS_SCHEMA_VERSION = 7;
export const PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT = 10;

/** TWS bin centers shown in Performance Drivers tables and trained in condition_model. */
export const PERFORMANCE_DRIVERS_TWS_BINS_KPH: number[] = [15, 20, 25, 30, 35, 40, 45];

export function performanceDriversTableTwsBins(
  envelope?: PerformanceDriversEnvelopeSlice | null
): number[] {
  const allowed = new Set(PERFORMANCE_DRIVERS_TWS_BINS_KPH);
  const fromEnvelope = envelope?.tws_bins ?? PERFORMANCE_DRIVERS_TWS_BINS_KPH;
  return fromEnvelope.filter((bin) => allowed.has(bin));
}

/** Config groups with at least one trained (non-skipped) cell for the point of sail. */
export function availablePerformanceDriversConfigurations(
  envelope: PerformanceDriversEnvelopeSlice | null | undefined,
  pointOfSail: string,
  schemaVersion: number
): string[] {
  if (!envelope || schemaVersion < 2) return [];

  const trained = new Set<string>();
  for (const cell of envelope.condition_cells ?? []) {
    if (cell.point_of_sail !== pointOfSail || !cell.configuration || cell.skipped) continue;
    trained.add(cell.configuration);
  }

  const fromEnvelope = envelope.configuration_options ?? [];
  const ordered = fromEnvelope.filter((config) => trained.has(config));
  if (ordered.length > 0) return ordered;
  return [...trained].sort();
}

export type PointOfSail = "UPWIND" | "DOWNWIND" | "REACHING";

export const POINT_OF_SAIL_OPTIONS: PointOfSail[] = ["UPWIND", "DOWNWIND", "REACHING"];

/** BSP/TWA show SHAP values but are excluded from importance heatmap coloring. */
export const IMPORTANCE_HEATMAP_EXCLUDED_COLUMNS = new Set(["Bsp_kph", "Twa_n_deg"]);

export function isImportanceHeatmapColumn(column: string): boolean {
  return !IMPORTANCE_HEATMAP_EXCLUDED_COLUMNS.has(column);
}

/** Min/max SHAP for heatmap coloring within a single TWS row (setup channels only). */
export function importanceHeatmapDomainForRow(
  row: PerformanceDriversTableRow,
  columns: string[]
): [number, number] {
  const values: number[] = [];
  for (const col of columns) {
    if (!isImportanceHeatmapColumn(col)) continue;
    const v = row.importanceValues[col];
    if (v != null && Number.isFinite(v)) values.push(v);
  }
  if (values.length === 0) return [0, 1];
  return [Math.min(...values), Math.max(...values)];
}

/** Sum of mean |SHAP| for heatmap columns in one TWS row (setup channels only). */
export function importanceHeatmapRowTotal(
  row: PerformanceDriversTableRow,
  columns: string[]
): number {
  let total = 0;
  for (const col of columns) {
    if (!isImportanceHeatmapColumn(col)) continue;
    const v = row.importanceValues[col];
    if (v != null && Number.isFinite(v)) total += v;
  }
  return total;
}

/** Share of row setup-driver sensitivity (0–100), or null when undefined. */
export function importanceSharePercentInRow(
  row: PerformanceDriversTableRow,
  column: string,
  columns: string[]
): number | null {
  const v = row.importanceValues[column];
  if (v == null || !Number.isFinite(v)) return null;
  const total = importanceHeatmapRowTotal(row, columns);
  if (total <= 0) return null;
  return (v / total) * 100;
}

/** Minimum |r| to treat higher channel values as clearly faster/slower on target. */
export const IMPORTANCE_DIRECTION_CORR_MIN = 0.2;

export function pearsonCorrelation(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 3 || ys.length !== n) return null;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let denX = 0;
  let denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const den = Math.sqrt(denX * denY);
  if (den <= 0 || !Number.isFinite(den)) return null;
  return num / den;
}

/** Whether higher channel values tend to raise (+) or lower (−) predicted target. */
export function shapDirectionCorrelation(points: ShapBeeswarmFeaturePoint[]): number | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const p of points) {
    if (p.value != null && Number.isFinite(p.value) && Number.isFinite(p.shap)) {
      xs.push(p.value);
      ys.push(p.shap);
    }
  }
  return pearsonCorrelation(xs, ys);
}

export type ImportanceDirectionKind = "faster" | "slower" | "neutral";

export function importanceDirectionKind(correlation: number | null | undefined): ImportanceDirectionKind {
  if (correlation == null || !Number.isFinite(correlation)) return "neutral";
  if (correlation >= IMPORTANCE_DIRECTION_CORR_MIN) return "faster";
  if (correlation <= -IMPORTANCE_DIRECTION_CORR_MIN) return "slower";
  return "neutral";
}

/** Intensity bucket 0–5 from share % (drives background strength). */
export function importanceIntensityBucket(sharePercent: number | null | undefined): number {
  if (sharePercent == null || !Number.isFinite(sharePercent) || sharePercent <= 0) return 0;
  return Math.max(0, Math.min(5, Math.round(sharePercent / 6)));
}

export function importanceDisplayCellClasses(
  sharePercent: number | null | undefined,
  direction: number | null | undefined,
  colored: boolean
): string {
  if (!colored) return "";
  if (sharePercent == null || !Number.isFinite(sharePercent)) return "performance-drivers-cell-empty";
  const kind = importanceDirectionKind(direction);
  const intensity = importanceIntensityBucket(sharePercent);
  if (intensity === 0 && kind === "neutral") return "performance-drivers-importance-flat";
  return `performance-drivers-importance-cell performance-drivers-importance-${kind} performance-drivers-importance-int-${intensity}`;
}

export function formatImportanceSharePercent(sharePercent: number | null | undefined): string {
  if (sharePercent == null || !Number.isFinite(sharePercent)) return "—";
  if (sharePercent > 0 && sharePercent < 1) return "<1%";
  return `${Math.round(sharePercent)}%`;
}

function heatmapCellClass(value: number, domain: [number, number]): string {
  const [min, max] = domain;
  if (min === max) return "c3";
  const t = (value - min) / (max - min);
  const idx = Math.round(Math.max(0, Math.min(6, t * 6)));
  return `c${idx}`;
}

/** Per-column min/max for straight-line heatmap coloring across TWS rows. */
export function straightLineColumnScales(
  rows: PerformanceDriversTableRow[],
  columns: string[]
): Record<string, [number, number]> {
  const scales: Record<string, [number, number]> = {};
  for (const col of columns) {
    const values: number[] = [];
    for (const row of rows) {
      if (row.straightLineEmpty ?? row.skipped) continue;
      const v = row.channelValues[col];
      if (v != null && Number.isFinite(v)) values.push(v);
    }
    if (values.length === 0) continue;
    scales[col] = [Math.min(...values), Math.max(...values)];
  }
  return scales;
}

export function straightLineCellHeatClass(
  value: number | null | undefined,
  column: string,
  scales: Record<string, [number, number]>
): string {
  if (value == null || !Number.isFinite(value)) return "performance-drivers-cell-empty";
  const domain = scales[column];
  if (!domain) return "";
  return heatmapCellClass(value, domain);
}

const VMG_COLUMN_NAMES = new Set(["Vmg_kph", "Vmg_perc"]);
const REACHING_EXCLUDED_COLUMNS = new Set(["Bsp_kph", ...VMG_COLUMN_NAMES]);

export interface PerformanceDriversConditionCell {
  condition_key: string;
  tws_bin: number;
  point_of_sail: string;
  configuration?: string;
  n_samples: number;
  n_boats?: number;
  skipped: boolean;
  skip_reason?: string;
  rmse?: number | null;
  rmse_train?: number | null;
  fit?: PerformanceDriversFit | null;
  channel_means?: Record<string, number>;
  straight_line_channels?: Record<string, number>;
  straight_line_record_count?: number | null;
  features?: ShapBeeswarmFeature[];
}

export interface PerformanceDriversTableRow {
  twsBin: number;
  skipped: boolean;
  skipReason?: string;
  /** True when best-modes straight-line API returned no data for this TWS band. */
  straightLineEmpty?: boolean;
  /** Best-modes segment count for this TWS band (straight-line table Count column). */
  sampleCount: number | null;
  channelValues: Record<string, number | null>;
  /** Mean |SHAP| in kph (tooltip / reference columns). */
  importanceValues: Record<string, number | null>;
  /** Share of row setup-driver sensitivity (0–100). */
  importanceSharePct: Record<string, number | null>;
  /** Pearson r between channel value and signed SHAP (−1 to 1). */
  importanceDirection: Record<string, number | null>;
}

export interface PerformanceDriversEnvelopeSlice {
  schema_version?: number;
  tws_bins?: number[];
  configuration_options?: string[];
  point_of_sail_options?: string[];
  feature_columns?: string[];
  feature_columns_by_point_of_sail?: Record<string, string[]>;
  feature_display_names?: Record<string, string>;
  target_unit_by_point_of_sail?: Record<string, string>;
  condition_cells?: PerformanceDriversConditionCell[];
  generated_at?: string;
  date_range?: { start: string; end: string };
}

export function slugifyConfiguration(config: string): string {
  return config
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "") || "unknown";
}

export function conditionKey(
  configuration: string | undefined,
  twsBin: number,
  pointOfSail: string,
  schemaVersion: number
): string {
  const posSlug = pointOfSail.toLowerCase();
  if (schemaVersion >= 2 && configuration) {
    return `config_${slugifyConfiguration(configuration)}_tws_${twsBin}_${posSlug}`;
  }
  return `tws_${twsBin}_${posSlug}`;
}

/** GP50 maneuver-style TWS dropdown: 5 kph steps with ±2.5 kph cell matching. */
export const GP50_TWS_DROPDOWN_STEP_KPH = 5;
export const GP50_TWS_BIN_HALF_WIDTH_KPH = 2.5;

export function generateGp50TwsDropdownOptionsKph(cellBinCentersKph: number[]): number[] {
  if (cellBinCentersKph.length === 0) return [];
  const minTws = Math.min(...cellBinCentersKph);
  const maxTws = Math.max(...cellBinCentersKph);
  const minBin = Math.floor(minTws / GP50_TWS_DROPDOWN_STEP_KPH) * GP50_TWS_DROPDOWN_STEP_KPH;
  const maxBin = Math.ceil(maxTws / GP50_TWS_DROPDOWN_STEP_KPH) * GP50_TWS_DROPDOWN_STEP_KPH;
  const options: number[] = [];
  for (let bin = minBin; bin <= maxBin; bin += GP50_TWS_DROPDOWN_STEP_KPH) {
    options.push(bin);
  }
  return options;
}

export function resolveBackendTwsBinKph(selectedKph: number, backendBins: number[]): number | null {
  if (backendBins.length === 0) return null;
  const minTws = selectedKph - GP50_TWS_BIN_HALF_WIDTH_KPH;
  const maxTws = selectedKph + GP50_TWS_BIN_HALF_WIDTH_KPH;
  const inRange = backendBins.filter((b) => b >= minTws && b <= maxTws);
  const pool = inRange.length > 0 ? inRange : backendBins;
  return pool.reduce((best, b) => (Math.abs(b - selectedKph) < Math.abs(best - selectedKph) ? b : best));
}

export function tableColumnsForPointOfSail(
  envelope: PerformanceDriversEnvelopeSlice | null | undefined,
  pointOfSail: string
): string[] {
  const fromEnvelope = envelope?.feature_columns_by_point_of_sail?.[pointOfSail];
  const raw =
    fromEnvelope?.length
      ? [...fromEnvelope]
      : orderPerformanceDriversTableColumns(
          (envelope?.feature_columns ?? []).filter((col) => col !== "Tws_kph"),
          pointOfSail
        );
  return orderPerformanceDriversTableColumns(raw, pointOfSail);
}

/** Importance table: setup channels only (no BSP / TWA). */
export function importanceTableColumnsForPointOfSail(
  envelope: PerformanceDriversEnvelopeSlice | null | undefined,
  pointOfSail: string
): string[] {
  return tableColumnsForPointOfSail(envelope, pointOfSail).filter((col) => isImportanceHeatmapColumn(col));
}

/** Canonical table column order: BSP before TWA on up/down; no BSP/VMG on reaching. */
export function orderPerformanceDriversTableColumns(columns: string[], pointOfSail: string): string[] {
  const unique = [...new Set(columns.filter((col) => col && col !== "Tws_kph"))];
  if (pointOfSail === "REACHING") {
    return unique.filter((col) => !REACHING_EXCLUDED_COLUMNS.has(col));
  }

  const setup = unique.filter((col) => col !== "Bsp_kph" && col !== "Twa_n_deg" && !VMG_COLUMN_NAMES.has(col));
  const ordered: string[] = [];
  ordered.push("Bsp_kph");
  if (unique.includes("Twa_n_deg")) {
    ordered.push("Twa_n_deg");
  }
  for (const col of setup) {
    if (!ordered.includes(col)) {
      ordered.push(col);
    }
  }
  return ordered;
}

export function resolvePerformanceDriversTargetUnit(
  envelope: PerformanceDriversEnvelopeSlice | null | undefined,
  pointOfSail: string
): string {
  return envelope?.target_unit_by_point_of_sail?.[pointOfSail] ?? (pointOfSail === "REACHING" ? "BSP [KPH]" : "VMG [KPH]");
}

export function columnHeaderForFeature(
  column: string,
  displayNames?: Record<string, string>
): string {
  return displayNames?.[column] ?? GP50_STRAIGHT_LINE_DB_COLUMN_LABELS[column] ?? column;
}

export function formatPerformanceDriversSampleCount(count: number | null | undefined): string {
  if (count == null || !Number.isFinite(count)) return "—";
  return String(Math.round(count));
}

function straightLineValuesFromCell(
  cell: PerformanceDriversConditionCell | undefined,
  columns: string[],
  schemaVersion: number
): {
  channelValues: Record<string, number | null>;
  sampleCount: number | null;
  straightLineEmpty: boolean | undefined;
} {
  const channelValues: Record<string, number | null> = Object.fromEntries(
    columns.map((col) => [col, null])
  );
  const hasEmbeddedFields =
    schemaVersion >= 6 &&
    cell != null &&
    (cell.straight_line_channels != null || cell.straight_line_record_count != null);

  if (!hasEmbeddedFields) {
    return { channelValues, sampleCount: null, straightLineEmpty: undefined };
  }

  const count = cell.straight_line_record_count;
  const sl = cell.straight_line_channels ?? {};
  const hasEnough =
    count != null &&
    Number.isFinite(count) &&
    count >= PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT &&
    Object.keys(sl).length > 0;

  if (!hasEnough) {
    return { channelValues, sampleCount: null, straightLineEmpty: true };
  }

  for (const col of columns) {
    const value = sl[col];
    channelValues[col] = value != null && Number.isFinite(value) ? value : null;
  }
  return { channelValues, sampleCount: count, straightLineEmpty: false };
}

export function buildDriversTableRows(
  envelope: PerformanceDriversEnvelopeSlice | null | undefined,
  configuration: string | undefined,
  pointOfSail: string,
  schemaVersion: number,
  showConfigSelector: boolean
): PerformanceDriversTableRow[] {
  if (!envelope) return [];
  const columns = tableColumnsForPointOfSail(envelope, pointOfSail);
  const twsBins = performanceDriversTableTwsBins(envelope);
  const cells = envelope.condition_cells ?? [];

  return twsBins.map((twsBin) => {
    const key = conditionKey(
      showConfigSelector ? configuration : undefined,
      twsBin,
      pointOfSail,
      schemaVersion
    );
    const cell = cells.find((c) => c.condition_key === key);
    const skipped = !cell || cell.skipped;
    const straightLine = straightLineValuesFromCell(cell, columns, schemaVersion);
    const channelValues: Record<string, number | null> = { ...straightLine.channelValues };
    const importanceValues: Record<string, number | null> = {};
    const importanceSharePct: Record<string, number | null> = {};
    const importanceDirection: Record<string, number | null> = {};

    for (const col of columns) {
      if (skipped) {
        importanceValues[col] = null;
        importanceSharePct[col] = null;
        importanceDirection[col] = null;
        continue;
      }
      const shapFeat = cell.features?.find((f) => f.name === col);
      const shapVal = shapFeat?.mean_abs_shap;
      importanceValues[col] = shapVal != null && Number.isFinite(shapVal) ? shapVal : null;
      importanceDirection[col] = shapFeat?.points?.length
        ? shapDirectionCorrelation(shapFeat.points)
        : null;
    }

    const rowDraft: PerformanceDriversTableRow = {
      twsBin,
      skipped,
      skipReason: cell?.skip_reason,
      straightLineEmpty: straightLine.straightLineEmpty,
      sampleCount: straightLine.sampleCount,
      channelValues,
      importanceValues,
      importanceSharePct: {},
      importanceDirection,
    };
    for (const col of columns) {
      if (skipped) {
        rowDraft.importanceSharePct[col] = null;
        continue;
      }
      rowDraft.importanceSharePct[col] = isImportanceHeatmapColumn(col)
        ? importanceSharePercentInRow(rowDraft, col, columns)
        : null;
    }

    return rowDraft;
  });
}

export function needsPerformanceDriversSnapshotRebuild(
  envelopeOrFeatures?: PerformanceDriversEnvelopeSlice | Array<{ name?: string }> | null,
  schemaVersion?: number
): boolean {
  if (envelopeOrFeatures && !Array.isArray(envelopeOrFeatures)) {
    const envelope = envelopeOrFeatures;
    if (envelope.schema_version == null || envelope.schema_version < PERFORMANCE_DRIVERS_SCHEMA_VERSION) {
      return true;
    }
    const hasEmbeddedStraightLine = envelope.condition_cells?.some(
      (c) =>
        c.straight_line_channels != null &&
        Object.keys(c.straight_line_channels).length > 0 &&
        c.straight_line_record_count != null &&
        c.straight_line_record_count >= PERFORMANCE_DRIVERS_STRAIGHT_LINE_MIN_RECORD_COUNT
    );
    if (!hasEmbeddedStraightLine) {
      return true;
    }
    const hasTrainedShap = envelope.condition_cells?.some(
      (c) => !c.skipped && c.features && c.features.length > 0
    );
    return !hasTrainedShap;
  }
  const version = schemaVersion ?? (envelopeOrFeatures as PerformanceDriversEnvelopeSlice)?.schema_version;
  if (version != null && version >= PERFORMANCE_DRIVERS_SCHEMA_VERSION) {
    return false;
  }
  const features = Array.isArray(envelopeOrFeatures) ? envelopeOrFeatures : undefined;
  if (!features?.length) return false;
  return !features.some((f) => f.name && PERFORMANCE_FEATURE_NAMES.has(f.name));
}

export function getTestRmseTrustBadge(cell: PerformanceDriversCell | null | undefined): TrustBadge {
  return getPerformanceDriversConfidenceBadge(cell);
}

export function getTrainRmseTrustBadge(cell: PerformanceDriversCell | null | undefined): TrustBadge {
  return getPerformanceDriversConfidenceBadge(cell);
}

/** @deprecated Use getPerformanceDriversConfidenceBadge */
export function getModelTrustBadge(cell: PerformanceDriversCell | null | undefined): TrustBadge {
  return getPerformanceDriversConfidenceBadge(cell);
}

export function getSegmentQualityBadge(count: number): QualityBadge {
  if (count >= SEGMENTS_GOOD_MIN) return { level: "good", label: "Good" };
  if (count >= SEGMENTS_MODERATE_MIN) return { level: "moderate", label: "Moderate" };
  return { level: "bad", label: "Bad" };
}

export function getBoatQualityBadge(count: number): QualityBadge {
  if (count >= BOATS_GOOD_MIN) return { level: "good", label: "Good" };
  if (count >= BOATS_MODERATE_MIN) return { level: "moderate", label: "Moderate" };
  return { level: "bad", label: "Bad" };
}

export function getTestSegmentQualityBadge(count: number): QualityBadge {
  if (count >= TEST_SEGMENTS_GOOD_MIN) return { level: "good", label: "Good" };
  if (count >= TEST_SEGMENTS_MODERATE_MIN) return { level: "moderate", label: "Moderate" };
  return { level: "bad", label: "Bad" };
}

export function isPerformanceChannel(name: string, label?: string): boolean {
  const haystack = `${name} ${label ?? ""}`.toLowerCase();
  return /tws|twa_n|twa_deg|\bbsp\b|lwy_n|\blwy\b/.test(haystack);
}
